import { parseISO } from 'date-fns';
import type { PollQuestion } from 'models/poll';
import type {
  CommunityPollContentObjectDetailDataObject,
  CommunityPollContentObjectListDataObject,
  PollContentObjectDetailDataObject,
  PollContentObjectListDataObject,
  ProgramPollContentObjectDetailDataObject,
  ProgramPollContentObjectListDataObject,
} from '../../service';
import {
  type CommunityContentObjectDetail,
  type CommunityContentObjectList,
  type ProgramContentObjectDetail,
  type ProgramContentObjectList,
  createCommunityContentObjectDetail,
  createCommunityContentObjectList,
  createProgramContentObjectDetail,
  createProgramContentObjectList,
} from '../contentable-type-content-objects';

export type PollCommunityContentObjectDetail = {
  poll: {
    description: string;
    endTime: Date;
    htmlDescription: string;
    id: string;
    questions: PollQuestion[];
    startTime: Date;
    title: string;
  };
  type: 'poll';
} & CommunityContentObjectDetail;

export type PollCommunityContentObjectList = {
  poll: {
    description: string;
    endTime: Date;
    htmlDescription: string;
    id: string;
    questions: PollQuestion[];
    startTime: Date;
    title: string;
  };
  type: 'poll';
} & CommunityContentObjectList;

export type PollContentObjectDetail = PollCommunityContentObjectDetail | ProgramPollContentObjectDetail;

export type PollContentObjectList = PollCommunityContentObjectList | ProgramPollContentObjectList;

export type ProgramPollContentObjectDetail = {
  poll: {
    description: string;
    endTime: Date;
    htmlDescription: string;
    id: string;
    questions: PollQuestion[];
    startTime: Date;
    title: string;
  };
  type: 'poll';
} & ProgramContentObjectDetail;

export type ProgramPollContentObjectList = {
  poll: {
    description: string;
    endTime: Date;
    htmlDescription: string;
    id: string;
    questions: PollQuestion[];
    startTime: Date;
    title: string;
  };
  type: 'poll';
} & ProgramContentObjectList;

export const createPollCommunityContentObjectDetail = (
  data: CommunityPollContentObjectDetailDataObject,
): PollCommunityContentObjectDetail => ({
  ...createCommunityContentObjectDetail(data),
  poll: {
    description: data.poll.description,
    endTime: parseISO(data.poll.end_time),
    htmlDescription: data.poll.html_description,
    id: data.poll.id,
    questions: data.poll.questions.map((question) => ({
      choices: question.choices.map((choice) => ({
        id: choice.id,
        name: choice.name,
        voted: choice.voted,
        votesCount: choice.votes_count,
      })),
      id: question.id,
      multiple: question.multiple,
      question: question.question,
      questionVotes: question.question_votes,
    })),
    startTime: parseISO(data.poll.start_time),
    title: data.poll.title,
  },
  type: 'poll',
});

export const createPollCommunityContentObjectList = (
  data: CommunityPollContentObjectListDataObject,
): PollCommunityContentObjectList => ({
  ...createCommunityContentObjectList(data),
  poll: {
    description: data.poll.description,
    endTime: parseISO(data.poll.end_time),
    htmlDescription: data.poll.html_description,
    id: data.poll.id,
    questions: data.poll.questions.map((question) => ({
      choices: question.choices.map((choice) => ({
        id: choice.id,
        name: choice.name,
        voted: choice.voted,
        votesCount: choice.votes_count,
      })),
      id: question.id,
      multiple: question.multiple,
      question: question.question,
      questionVotes: question.question_votes,
    })),
    startTime: parseISO(data.poll.start_time),
    title: data.poll.title,
  },
  type: 'poll',
});

export const createProgramPollContentObjectDetail = (
  data: ProgramPollContentObjectDetailDataObject,
): ProgramPollContentObjectDetail => ({
  ...createProgramContentObjectDetail(data),
  poll: {
    description: data.poll.description,
    endTime: parseISO(data.poll.end_time),
    htmlDescription: data.poll.html_description,
    id: data.poll.id,
    questions: data.poll.questions.map((question) => ({
      choices: question.choices.map((choice) => ({
        id: choice.id,
        name: choice.name,
        voted: choice.voted,
        votesCount: choice.votes_count,
      })),
      id: question.id,
      multiple: question.multiple,
      question: question.question,
      questionVotes: question.question_votes,
    })),
    startTime: parseISO(data.poll.start_time),
    title: data.poll.title,
  },
  type: 'poll',
});

export const createProgramPollContentObjectList = (
  data: ProgramPollContentObjectListDataObject,
): ProgramPollContentObjectList => ({
  ...createProgramContentObjectList(data),
  poll: {
    description: data.poll.description,
    endTime: parseISO(data.poll.end_time),
    htmlDescription: data.poll.html_description,
    id: data.poll.id,
    questions: data.poll.questions.map((question) => ({
      choices: question.choices.map((choice) => ({
        id: choice.id,
        name: choice.name,
        voted: choice.voted,
        votesCount: choice.votes_count,
      })),
      id: question.id,
      multiple: question.multiple,
      question: question.question,
      questionVotes: question.question_votes,
    })),
    startTime: parseISO(data.poll.start_time),
    title: data.poll.title,
  },
  type: 'poll',
});

export const createPollContentObjectDetail = (data: PollContentObjectDetailDataObject): PollContentObjectDetail => {
  switch (data.contentable_type) {
    case 'Community':
      return createPollCommunityContentObjectDetail(data);
    case 'Program':
      return createProgramPollContentObjectDetail(data);
    default:
      throw new Error('Unknown contentable type');
  }
};

export const createPollContentObjectList = (data: PollContentObjectListDataObject): PollContentObjectList => {
  switch (data.contentable_type) {
    case 'Community':
      return createPollCommunityContentObjectList(data);
    case 'Program':
      return createProgramPollContentObjectList(data);
    default:
      throw new Error('Unknown contentable type');
  }
};
